import { HttpContext, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { SessionService } from '../auth/session.service';
import { promiseWithCatch } from '../common/app.helpers';
import ProjectLinks from '../common/project.links';
import { InformationMessage } from '../interfaces';
import { NotificationService } from '../notifications/notifications.service';
import { ClientErrorResponse } from '../swagger-api';
import {
  AccountDeactivated,
  AccountDisabledException,
  AccountUndecidedException,
  AdminEmailAlreadyTaken,
  BaseAPIException,
  ChangedEmailIsNotVerified,
  CommonException,
  EmailNotVerified,
  EmailVerificationFailed,
  EmailVerificationLinkExpired,
  extractError,
  FileNotDownload,
  NotFoundException,
  PasswordRecoveryProhibited,
  UnauthorizedException,
  UnknownException,
} from './api-error';

@Injectable({ providedIn: 'root' })
export class ErrorService {
  public get authorizationLostTrigger$(): Observable<InformationMessage> {
    return this.authorizationLost$.asObservable();
  }

  public get reactivatePetitionTriggers$(): Observable<string> {
    return this.reactivatePetition$.asObservable();
  }

  public get emailNotVerifiedTrigger$(): Observable<string> {
    return this.emailNotVerified$.asObservable();
  }

  public get requestChangedEmailVerification(): Observable<string> {
    return this.requestChangedEmailVerification$.asObservable();
  }

  private reactivatePetition$: Subject<string> = new Subject<string>();
  private emailNotVerified$: Subject<string> = new Subject<string>();
  private requestChangedEmailVerification$: Subject<string> = new Subject<string>();
  private authorizationLost$: Subject<InformationMessage> = new Subject<InformationMessage>();

  private readonly navigate: (url: string) => void = (url: string) => promiseWithCatch(this.router.navigateByUrl(url));

  private readonly apiExceptions: BaseAPIException[] = [
    new UnknownException(),
    new EmailVerificationFailed(),
    new FileNotDownload(),
    new NotFoundException(() => this.navigate(ProjectLinks.LINK_DEFAULT)),
    new EmailNotVerified((email: string) => this.emailNotVerified$.next(email)),
    new ChangedEmailIsNotVerified((email: string) => this.requestChangedEmailVerification$.next(email)),
    new EmailVerificationLinkExpired((email: string) => this.emailNotVerified$.next(email)),
    new PasswordRecoveryProhibited(
      (email: string) => this.emailNotVerified$.next(email),
      () => this.navigate(ProjectLinks.LINK_LOGIN),
    ),
    new AdminEmailAlreadyTaken(() => this.navigate(ProjectLinks.LINK_LOGIN)),
    new AccountDeactivated(
      (email: string) => this.reactivatePetition$.next(email),
      () => this.navigate(ProjectLinks.LINK_TERMS),
    ),
    new AccountUndecidedException(),
    new AccountDisabledException(),
    new UnauthorizedException(),
  ];

  private router = inject(Router);
  private sessionService = inject(SessionService);
  private notificationService = inject(NotificationService);

  public handle(httpError: HttpErrorResponse, context: HttpContext): void {
    const status: HttpStatusCode = httpError.status;
    const error: ClientErrorResponse = extractError(httpError.error as object);

    const exception: BaseAPIException =
      this.apiExceptions.find((exception: BaseAPIException) => exception.match(status, error, context)) ??
      new CommonException();

    const { level, messsage, action } = exception.summarize(error, context);

    action();

    if (this.sessionService.isSessionActive() && exception instanceof UnauthorizedException) {
      this.authorizationLost$.next(messsage);
      return;
    }

    if (level === 'BLOCKING') {
      this.notificationService.informationDialog(messsage);
      return;
    }

    this.notificationService.showFailureMessage(messsage);
  }
}

@Injectable()
export class FakeErrorService extends ErrorService {
  public override get authorizationLostTrigger$(): Observable<InformationMessage> {
    return this.fakeAuthorizationLostTrigger$.asObservable();
  }

  public override get reactivatePetitionTriggers$(): Observable<string> {
    return this.fakeReactivatePetition$.asObservable();
  }

  public override get emailNotVerifiedTrigger$(): Observable<string> {
    return this.fakeEmailNotVerified$.asObservable();
  }

  public override get requestChangedEmailVerification(): Observable<string> {
    return this.fakeRequestChangedEmailVerification$.asObservable();
  }

  public override handle(_httpError: HttpErrorResponse, _context: HttpContext): void {
    //
  }

  public fakeAuthorizationLostTrigger$: Subject<InformationMessage> = new Subject<InformationMessage>();
  public fakeReactivatePetition$: Subject<string> = new Subject<string>();
  public fakeEmailNotVerified$: Subject<string> = new Subject<string>();
  public fakeRequestChangedEmailVerification$: Subject<string> = new Subject<string>();
}
